import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import { normalizeString } from '../utils/stringUtils';
import api from '../services/api';
import BottomSheet from './BottomSheet';

// Cache simples para comandas físicas
const comandasCache = {
  data: [] as ComandaFisica[],
  timestamp: 0,
  TTL: 30000 // 30 segundos
};

// Função para limpar cache (útil quando uma comanda é selecionada)
export const clearComandasCache = () => {
  comandasCache.data = [];
  comandasCache.timestamp = 0;
};

interface ComandaFisica {
  id: number;
  numero: string;
  nome: string;
  ativo: boolean;
}

interface ComandaSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (comanda: ComandaFisica) => void;
  title: string;
  description?: string;
  excludeIds?: number[];
}

const ComandaSearchModal: React.FC<ComandaSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  title,
  description,
  excludeIds = []
}) => {
  const [comandas, setComandas] = useState<ComandaFisica[]>([]);
  const [filteredComandas, setFilteredComandas] = useState<ComandaFisica[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedComanda, setSelectedComanda] = useState<ComandaFisica | null>(null);
  const hasLoaded = useRef(false);
  const { isMobile } = useMobileOptimizations();
  
  // Carregar comandas físicas disponíveis
  const carregarComandas = useCallback(async () => {
    if (loading || hasLoaded.current) return;
    
    const now = Date.now();
    if (comandasCache.data.length > 0 && (now - comandasCache.timestamp) < comandasCache.TTL) {
      const comandasDisponiveis = comandasCache.data.filter((comanda: ComandaFisica) => 
        comanda.ativo && !excludeIds.includes(comanda.id)
      );
      setComandas(comandasDisponiveis);
      setFilteredComandas(comandasDisponiveis);
      hasLoaded.current = true;
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get('/api/garcom/comandas-fisicas');
      
      comandasCache.data = response.data;
      comandasCache.timestamp = now;
      
      const comandasDisponiveis = response.data.filter((comanda: ComandaFisica) => 
        comanda.ativo && !excludeIds.includes(comanda.id)
      );
      
      setComandas(comandasDisponiveis);
      setFilteredComandas(comandasDisponiveis);
      hasLoaded.current = true;
    } catch (error: any) {
      toast.error('Erro ao carregar comandas físicas');
    } finally {
      setLoading(false);
    }
  }, [loading, excludeIds]);

  // Filtrar comandas
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredComandas(comandas);
    } else {
      const term = normalizeString(searchTerm);
      const isNumeric = /^\d+$/.test(term);
      const filtered = comandas.filter(comanda => {
        const comandaNumeroNormalizado = normalizeString(comanda.numero);
        // Remove left zeros from both for exact numeric comparison if numeric
        const numMatch = isNumeric 
          ? comandaNumeroNormalizado.replace(/^0+/, '') === term.replace(/^0+/, '')
          : comandaNumeroNormalizado.includes(term);
          
        const nomeMatch = isNumeric ? false : normalizeString(comanda.nome).includes(term);
        return numMatch || nomeMatch;
      });
      setFilteredComandas(filtered);
    }
  }, [searchTerm, comandas]);

  useEffect(() => {
    if (isOpen) {
      carregarComandas();
      setSearchTerm('');
    } else {
      hasLoaded.current = false;
      setSelectedComanda(null);
    }
  }, [isOpen, carregarComandas]);

  const handleSelect = (comanda: ComandaFisica) => {
    onSelect(comanda);
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxHeight={isMobile ? '100dvh' : '600px'}
      fullHeight={isMobile}
      className="comanda-search-modal"
    >
      <div className="flex flex-col h-full bg-[#111318]">
        {/* Search Header */}
        <div className="p-4 sm:p-6 space-y-4">
          {description && (
            <p className="text-gray-400 text-sm leading-relaxed px-1">
              {description}
            </p>
          )}
          
          <div className="relative group">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors ${searchTerm ? 'text-orange-500' : 'text-gray-500 group-focus-within:text-orange-500'}`} />
            <input
              type="text"
              inputMode="numeric"
              placeholder="Digite o número da comanda..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all text-lg"
              autoFocus={!isMobile}
            />
          </div>
        </div>

        {/* List Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-6 custom-scrollbar">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filteredComandas.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-gray-600" />
              </div>
              <p className="text-gray-400 font-medium">
                {searchTerm ? `Nenhuma comanda "${searchTerm}" encontrada` : 'Nenhuma comanda disponível no momento.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredComandas.map((comanda) => (
                <button
                  key={comanda.id}
                  onClick={() => handleSelect(comanda)}
                  className="group relative w-full p-4 rounded-2xl border border-white/5 bg-white/5 hover:bg-white/10 hover:border-orange-500/30 transition-all text-left active:scale-[0.98] flex items-center justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl font-black text-white group-hover:text-orange-400 transition-colors leading-none">
                        {comanda.numero}
                      </span>
                    </div>
                    {comanda.nome && comanda.nome !== comanda.numero && (
                      <p className="text-sm text-gray-400 truncate font-medium">
                        {comanda.nome}
                      </p>
                    )}
                  </div>
                  
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-orange-500/20 transition-all">
                    <Check className="h-5 w-5 text-transparent group-hover:text-orange-500 transition-all" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default ComandaSearchModal;
